<template>
  <v-container fluid>
    <v-card>
      <v-toolbar color="primary" density="comfortable">
        <v-toolbar-title class="text-white"> Pièces détachées </v-toolbar-title>
        <v-spacer />
        <div class="d-flex ga-2">
          <table-header-buttons :attr="toolTipText" @add="add" @remove="remove" @reload="reloadData"
            @export="exportTable" @search="updateSearchPanel" :hasSelectedLines="selectedSparepartIds.length > 0" />
        </div>
      </v-toolbar>

      <v-row class="px-4 py-2" align="center" density="compact">
        <v-col cols="12">
          <div class="d-flex flex-wrap ga-4">
            <div class="text-caption text-medium-emphasis mb-2">Fabricant</div>
            <v-switch v-model="speed_queen" label="Speed Queen" color="success" density="compact" inset hide-details />
            <v-switch v-model="lg" label="LG" color="info" density="compact" inset hide-details />
            <v-switch v-model="maytag" label="Maytag" color="warning" density="compact" inset hide-details />
            <v-switch v-model="primus" label="Primus" color="primary" density="compact" inset hide-details />
          </div>
        </v-col>
      </v-row>

      <!-- Type Filters -->
      <v-row class="px-4 py-2" align="center" density="compact">
        <v-col cols="12">
          <div class="d-flex flex-wrap ga-4">
            <div class="text-caption text-medium-emphasis mb-2">Machine</div>
            <v-switch v-model="lave_linge" label="LL" color="error" density="compact" inset hide-details />
            <v-switch v-model="seche_linge" label="SL" color="warning" density="compact" inset hide-details />
            <v-switch v-model="stacked_lave_linge" label="SLL" color="info" density="compact" inset hide-details />
            <v-switch v-model="stacked_seche_linge" label="SSL" color="primary" density="compact" inset hide-details />
            <v-switch v-model="condensateur" label="Condensateur" color="pink" density="compact" inset hide-details />
            <v-switch v-model="armoire_a_lessive" label="Armoire" color="green" density="compact" inset hide-details />
            <v-switch v-model="payment_terminal" label="Terminal" color="teal" density="compact" inset hide-details />
            <v-switch v-model="balon_eau_chaude" label="Balon" color="deep-orange" density="compact" inset
              hide-details />
            <v-switch v-model="kit_effraction" label="Kit" color="indigo" density="compact" inset hide-details />
          </div>
        </v-col>
      </v-row>

      <Table :loading="loading" :searchField="true" :columns="columns" :items="spareparts" :showSelect="true" storageKey="sparePartTablePaginationState"
        @edit="edit" @rowClicked="rowClicked" @selectionChanged="selectionChanged" />
    </v-card>

    <!-- Overlay -->
    <v-overlay :model-value="loading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>

    <!-- Search panel -->
    <search-panel :rightDrawer="rightDrawer" @cancelSearch="cancelSearch" @searchData="searchSpareparts">
      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field label="Nom" v-model="searchFilter.contain.name" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field label="Reference" v-model="searchFilter.contain.reference" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field label="Telephone" v-model="searchFilter.contain.telephone" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="11" offset="1">
          <v-text-field label="Courriel" v-model="searchFilter.contain.email" />
        </v-col>
      </v-row>
    </search-panel>

    <ConfirmDialog v-model="dialog" :dialogTitle="dialogTitle" :dialogText="dialogText" @onfirm="onConfirm"
      @cancel="onCancel" confirm-button-text="Confirmer" cancel-button-text="Annuler" />
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";

import Table from "@/components/Table.vue";
import TableHeaderButtons from "@/components/TableHeaderButtons.vue";
import SearchPanel from "@/components/SearchPanel.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";

import {
  buildSearchFilters,
  buildJsonServerQuery,
} from "@/utils/app-util";

import { useAppStore } from "@/store/app";
import { createAndSaveDocument } from "@/utils/docx-util";
import { getSparePartExport, ProductMake, ProductType, SparePart, TableColumn } from "@/types";

import {
  deleteSparepart,
  fetchFilteredSpareParts,
  getSparepartById,
  searchForSpareparts,
} from "@/utils/sparepart-api";

const router = useRouter();
const appStore = useAppStore();

const loading = ref(false);
const dialog = ref(false);
const dialogTitle = ref("Suppression");
const dialogText = ref("Voulez-vous supprimer les pièces sélectionnées?");
const showSearchPanel = ref(false);

const spareparts = ref<SparePart[]>([]);
const selectedSparepartIds = ref<string[]>([]);

const columns: TableColumn[] = [
  { title: "Machine", key: "sparepartType", align: "start" },
  { title: "Status", key: "sparepartStatus" },
  { title: "Fabricant", key: "sparepartMake" },
  { title: "Désignation JLA", key: "name" },
  { title: "Référence", key: "reference" },
  { title: "Désignation française", key: "frenchName" },
  { title: "Prix (€)", key: "price" },
  { title: "Solde", key: "unitInStock" },
  { title: "Solde minimum", key: "minUnitInStock" },
  { title: "Commande", key: "command" },
  { title: "Total stock (€)", key: "stockValue" },
  { title: "", key: "actions", sortable: false },
];

const searchFilter = ref({
  contain: {
    name: "",
    reference: "",
    telephone: "",
    email: "",
  },
});

const speed_queen = ref(true);
const lg = ref(false);
const primus = ref(false);
const maytag = ref(false);

const lave_linge = ref(true);
const seche_linge = ref(false);
const stacked_lave_linge = ref(false);
const stacked_seche_linge = ref(false);
const condensateur = ref(false);
const armoire_a_lessive = ref(false);
const payment_terminal = ref(false);
const balon_eau_chaude = ref(false);
const kit_effraction = ref(false);

const query = ref("");

const toolTipText = computed(() => "une pièce détachée");

const rightDrawer = computed({
  get: () => showSearchPanel.value,
  set: (value: boolean) => (showSearchPanel.value = value),
});

watch(
  [
    lg,
    primus,
    maytag,
    speed_queen,
    lave_linge,
    seche_linge,
    stacked_lave_linge,
    stacked_seche_linge,
    condensateur,
    armoire_a_lessive,
    payment_terminal,
    balon_eau_chaude,
    kit_effraction,
  ],
  () => {
    saveFiltersToLocalStorage();
    fetchSpareparts();
  }
);

function selectionChanged(selected: string[]) {
  selectedSparepartIds.value = [...selected];
}

function rowClicked(item: SparePart) {
  router.push(`spareparts/${item.id}`);
}

function edit(item: SparePart) {
  router.push(`spareparts/${item.id}`);
}

function add() {
  router.push("newsparepart");
}

function remove() {
  dialog.value = true;
}

async function onConfirm() {
  try {
    loading.value = true;
    dialog.value = false;

    await deleteSparepart(selectedSparepartIds.value);
    await fetchSpareparts();
    selectedSparepartIds.value = [];
    appStore.sendSuccessNotice("Sparepart has been deleted.");
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

function onCancel() {
  dialog.value = false;
}

function searchSpareparts() {
  showSearchPanel.value = false;
  buildSearchFilters(searchFilter.value);
  query.value = buildJsonServerQuery(searchFilter.value);
  _searchSpareparts(query.value);
}

async function _searchSpareparts(searchQuery: string) {
  try {
    loading.value = true;
    spareparts.value = await searchForSpareparts("spareparts?" + searchQuery);
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

function reloadData() {
  query.value = "";
  fetchSpareparts();
}

const exportTable = async () => {
  if (loading.value) {
    return;
  }

  const sparepartIds = [...selectedSparepartIds.value];
  if (!sparepartIds.length) {
    return;
  }

  try {
    loading.value = true;

    const selectedSpareparts = await Promise.all(
      sparepartIds.map((id) => getSparepartById(id))
    );

    createAndSaveDocument(
      "export_piecesDetachees",
      "../templates/ExportPiecesDetacheesTemplate.docx",
      getSparePartExport(selectedSpareparts)
    );
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
};

function updateSearchPanel() {
  rightDrawer.value = !rightDrawer.value;
}

function cancelSearch() {
  showSearchPanel.value = false;
}

function saveFiltersToLocalStorage() {
  localStorage.setItem(
    "sparePartTableFilters",
    JSON.stringify({
      lg: lg.value,
      primus: primus.value,
      maytag: maytag.value,
      speed_queen: speed_queen.value,
      lave_linge: lave_linge.value,
      seche_linge: seche_linge.value,
      stacked_lave_linge: stacked_lave_linge.value,
      stacked_seche_linge: stacked_seche_linge.value,
      condensateur: condensateur.value,
      armoire_a_lessive: armoire_a_lessive.value,
      payment_terminal: payment_terminal.value,
      balon_eau_chaude: balon_eau_chaude.value,
      kit_effraction: kit_effraction.value,
    })
  );
}

function loadFiltersFromLocalStorage() {
  const saved = localStorage.getItem("sparePartTableFilters");
  if (!saved) return;

  try {
    const f = JSON.parse(saved);
    lg.value = !!f.lg;
    primus.value = !!f.primus;
    maytag.value = !!f.maytag;
    speed_queen.value = !!f.speed_queen;
    lave_linge.value = !!f.lave_linge;
    seche_linge.value = !!f.seche_linge;
    stacked_lave_linge.value = !!f.stacked_lave_linge;
    stacked_seche_linge.value = !!f.stacked_seche_linge;
    condensateur.value = !!f.condensateur;
    armoire_a_lessive.value = !!f.armoire_a_lessive;
    payment_terminal.value = !!f.payment_terminal;
    balon_eau_chaude.value = !!f.balon_eau_chaude;
    kit_effraction.value = !!f.kit_effraction;
  } catch {
    resetFilters();
  }
}

function resetFilters() {
  lg.value = false;
  primus.value = false;
  maytag.value = false;
  speed_queen.value = true;
  lave_linge.value = true;
  seche_linge.value = false;
  stacked_lave_linge.value = false;
  stacked_seche_linge.value = false;
  condensateur.value = false;
  armoire_a_lessive.value = false;
  payment_terminal.value = false;
  balon_eau_chaude.value = false;
  kit_effraction.value = false;
}

async function fetchSpareparts() {
  try {
    loading.value = true;

    const types: ProductType[] = [];
    if (lave_linge.value) types.push(ProductType.LAVE_LINGE);
    if (seche_linge.value) types.push(ProductType.SECHE_LINGE);
    if (stacked_lave_linge.value) types.push(ProductType.STACKED_LAVE_LINGE);
    if (stacked_seche_linge.value) types.push(ProductType.STACKED_SECHE_LINGE);
    if (condensateur.value) types.push(ProductType.CONDENSATEUR);
    if (armoire_a_lessive.value) types.push(ProductType.ARMOIRE_A_LESSIVE);
    if (payment_terminal.value) types.push(ProductType.PAYMENT_TERMINAL);
    if (balon_eau_chaude.value) types.push(ProductType.BALON_EAU_CHAUDE);
    if (kit_effraction.value) types.push(ProductType.KIT_EFFRACTION);

    const makes: ProductMake[] = [];
    if (lg.value) makes.push(ProductMake.LG);
    if (primus.value) makes.push(ProductMake.PRIMUS);
    if (maytag.value) makes.push(ProductMake.MAYTAG);
    if (speed_queen.value) makes.push(ProductMake.SPEED_QUEEN);

    spareparts.value = types.length || makes.length ? await fetchFilteredSpareParts(makes, types) : [];
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  loadFiltersFromLocalStorage();
  fetchSpareparts();
});
</script>
<style scoped>
.title {
  font-size: 1.5rem;
  font-weight: bold;
}
</style>
